import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GEAR_WIDE_ROLES, PEOPLE_WIDE_ROLES, type Role } from '@unit/shared';
import { useClaimRoles } from '../features/auth/useClaimRoles.js';

/**
 * Hides a route from a role that has no business on it.
 *
 * This is a convenience, not a control (REQUIREMENTS §2.2). Every endpoint
 * behind these screens re-checks the caller's claims server-side, and Firestore
 * rules check them again on the direct reads. Deleting this component would
 * make the app ugly and confusing; it would not make it insecure.
 */
export function RequireRole({ allow, children }: { allow: readonly Role[]; children: ReactNode }) {
  const { t } = useTranslation();
  const { roles, loading } = useClaimRoles();

  if (loading) {
    return <p className="p-6 text-slate-500">{t('home.loading')}</p>;
  }

  if (!roles.some((role) => allow.includes(role))) {
    return (
      <section
        role="alert"
        data-testid="forbidden"
        className="rounded-2xl bg-white p-6 text-sm text-red-600 shadow-sm ring-1 ring-slate-200"
      >
        {t('errors.forbidden')}
      </section>
    );
  }

  return <>{children}</>;
}

/** Roles that manage or view other people's units. A plain soldier is not one. */
export const COMMANDER_ROLES: readonly Role[] = [
  'admin',
  'company_commander',
  'company_xo',
  'csm',
  'acsm',
  'platoon_commander',
  'platoon_sergeant',
  'team_commander',
];

/** Platoon-level command: the commander and the sergeant have the same reach (story 30). */
export const PLATOON_LEADER_ROLES: readonly Role[] = ['platoon_commander', 'platoon_sergeant'];

/** The roles with the storeroom's reach (§2.2; the company command since story 35). */
export const STOREROOM_ROLES: readonly Role[] = GEAR_WIDE_ROLES;

/** Roles that reach the org structure and soldiers' files: commanders, plus the people-wide ones (never gear by themselves). */
export const PEOPLE_ROLES: readonly Role[] = [...COMMANDER_ROLES, 'chrs'];

/** Who decides profile change requests and edits any soldier's details. */
export const PERSONNEL_ROLES: readonly Role[] = PEOPLE_WIDE_ROLES;
